import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  AppBar,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  Link,
  List,
  MenuItem,
  Paper,
  Select,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useEffect, useState } from 'react'
import type { LoaderFunction } from 'react-router-dom'
import {
  Form,
  Link as RouterLink,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigation,
  useSubmit
} from 'react-router-dom'
import { createAlbum, getTopAlbums } from '../albums'
import AlbumListItem from '../components/album-list-item'
import { LoaderData } from '../types/react-router-extra-types'

export const loader = (async ({ request }) => {
  const url = new URL(request.url)
  const q = url.searchParams.get('q')
  const albums = await getTopAlbums(q)
  return { albums, q }
}) satisfies LoaderFunction

export async function action() {
  const album = await createAlbum()
  return { album }
}

const sortTypes = ['popularity', 'name', 'year']
type SortType = (typeof sortTypes)[number]

export default function Root() {
  const { albums, q } = useLoaderData() as LoaderData<typeof loader>
  const [sortOrder, setSortOrder] = useState<SortType>('popularity')
  const submit = useSubmit()
  const navigation = useNavigation()
  const location = useLocation()
  const theme = useTheme()

  // On phones the master/detail layout can't sit side by side, so we show
  // either the album list or the selected album, not both stacked (which would
  // bury the detail below a long list).
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isAlbumSelected = location.pathname.startsWith('/albums/')
  const showList = !isMobile || !isAlbumSelected
  const showDetail = !isMobile || isAlbumSelected

  // Whether a search navigation is currently in flight (used for the spinner).
  const searching =
    navigation.location != null &&
    new URLSearchParams(navigation.location.search).has('q')

  // Keep the search input in sync with the URL's `q` param so that
  // back/forward navigation and deep links reflect the active query.
  useEffect(() => {
    const input = document.getElementById('q') as HTMLInputElement | null
    if (input) input.value = q ?? ''
  }, [q])

  const handleSortOrderChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSortOrder(event.target.value as string)
  }

  const getSortedAlbums = () => {
    if (sortOrder === 'popularity') return albums // default order, popularity
    return [...albums].sort((a, b) => {
      if (sortOrder === 'year') {
        if (a.year === null) return 1
        if (b.year === null) return -1
        return b.year - a.year
      }
      return a.name.localeCompare(b.name) // sort by name
    })
  }
  const sortedAlbums = getSortedAlbums()

  return (
    <Container
      maxWidth="xl"
      sx={{ bgcolor: 'background.default', minHeight: '100vh' }}
    >
      <AppBar position="static">
        <Toolbar>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            underline="none"
            variant="h6"
            noWrap
          >
            Celebrate David Bowie!
          </Link>
        </Toolbar>
      </AppBar>
      <Grid container spacing={{ xs: 0, sm: 4 }}>
        {showList && (
          <Grid item xs={12} sm={4} md={3}>
            <Paper sx={{ p: 2, my: 2 }}>
            <Form
              id="search-form"
              role="search"
              // Live search runs on every change, so Enter is already reflected
              // in the URL. Prevent the native GET submit to avoid a duplicate
              // history entry.
              onSubmit={(event) => event.preventDefault()}
            >
              <TextField
                id="q"
                aria-label="Search albums"
                placeholder="Search"
                type="search"
                name="q"
                defaultValue={q ?? ''}
                fullWidth
                onChange={(event) => {
                  // Live search: submit on each keystroke. Replace the history
                  // entry while refining a query so Back returns to the unfiltered
                  // list rather than stepping through every keystroke.
                  const isFirstSearch = q == null
                  submit(event.currentTarget.form, {
                    replace: !isFirstSearch
                  })
                }}
              />
              <div id="search-spinner" aria-hidden hidden={!searching}>
                <CircularProgress size={20} sx={{ mt: 1 }} />
              </div>
              <div className="sr-only" aria-live="polite" />
            </Form>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel htmlFor="sort-order">Sort by</InputLabel>
              <Select
                id="sort-order"
                label="Sort by"
                value={sortOrder}
                // @ts-ignore TODO fix, complicated one
                onChange={handleSortOrderChange}
              >
                {sortTypes.map((sortType) => (
                  <MenuItem key={sortType} value={sortType}>
                    {sortType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <nav>
              {sortedAlbums.length ? (
                <List>
                  {sortedAlbums.map((album) => (
                    <AlbumListItem key={album.id} album={album} />
                  ))}
                </List>
              ) : (
                <Typography variant="body1">
                  <i>No albums</i>
                </Typography>
              )}
            </nav>
            </Paper>
          </Grid>
        )}
        {showDetail && (
          <Grid item xs={12} sm={8} md={9} sx={{ my: 2 }}>
            {isMobile && isAlbumSelected && (
              <Button
                component={RouterLink}
                to="/"
                startIcon={<ArrowBackIcon />}
                sx={{ mb: 1 }}
              >
                Back to albums
              </Button>
            )}
            <Outlet />
          </Grid>
        )}
      </Grid>
    </Container>
  )
}
